import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export default function PointTaskSelector({
  apiConfig,
  open,
  selectedTaskIds,
  onChange,
  disabled = false,
}) {
  const apiConfigRef = useRef(apiConfig);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  apiConfigRef.current = apiConfig;

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    apiConfigRef.current.fetchTaskInfo()
      .then((response) => {
        if (!active) return;
        setTasks((response.RobotResponse || []).filter((task) => !task.Deleted));
      })
      .catch((requestError) => {
        if (!active) return;
        setTasks([]);
        setError(
          requestError?.response?.data?.Message
          || requestError.message
          || 'Unable to load available tasks'
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const tasksById = useMemo(
    () => new Map(tasks.map((task) => [String(task.Id), task])),
    [tasks]
  );

  const addTask = (taskId) => {
    if (!taskId) return;
    onChange([...selectedTaskIds, String(taskId)]);
  };

  const moveTask = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= selectedTaskIds.length) return;
    const nextTaskIds = [...selectedTaskIds];
    [nextTaskIds[index], nextTaskIds[nextIndex]] = [nextTaskIds[nextIndex], nextTaskIds[index]];
    onChange(nextTaskIds);
  };

  const removeTask = (index) => {
    onChange(selectedTaskIds.filter((_, taskIndex) => taskIndex !== index));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Tasks at destination
      </Typography>
      <Typography variant="caption" color="text.secondary">
        These tasks run in order after the robot reaches the selected point.
      </Typography>

      <FormControl fullWidth size="small" sx={{ mt: 1.25 }} disabled={disabled || loading}>
        <InputLabel id="point-task-selector-label">Add a task</InputLabel>
        <Select
          labelId="point-task-selector-label"
          value=""
          label="Add a task"
          onChange={(event) => addTask(event.target.value)}
          renderValue={() => ''}
        >
          <MenuItem value="">
            <em>Select a task</em>
          </MenuItem>
          {tasks.map((task) => (
            <MenuItem key={task.Id} value={String(task.Id)}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {task.Name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {task.Type || 'Task'} · {task.Command}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading tasks…
          </Typography>
        </Stack>
      )}
      {error && <Alert severity="warning" sx={{ mt: 1.25 }}>{error}</Alert>}

      {!loading && selectedTaskIds.length === 0 && (
        <Paper
          variant="outlined"
          sx={{ mt: 1.25, p: 1.5, borderStyle: 'dashed', color: 'text.secondary' }}
        >
          <Typography variant="body2">
            No destination tasks selected. The robot will only move to the point.
          </Typography>
        </Paper>
      )}

      <Stack spacing={0.75} sx={{ mt: selectedTaskIds.length ? 1.25 : 0 }}>
        {selectedTaskIds.map((taskId, index) => {
          const task = tasksById.get(String(taskId));
          return (
            <Paper
              key={`${taskId}-${index}`}
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderColor: 'primary.light',
                backgroundColor: 'action.hover',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 26,
                  height: 26,
                  flex: '0 0 auto',
                  borderRadius: '50%',
                  color: 'primary.contrastText',
                  backgroundColor: 'primary.main',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {index + 1}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                  {task?.Name || 'Unavailable task'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {task ? `${task.Type || 'Task'} · ${task.Command}` : taskId}
                </Typography>
              </Box>
              <Tooltip title="Move task earlier">
                <span>
                  <IconButton
                    size="small"
                    disabled={disabled || index === 0}
                    onClick={() => moveTask(index, -1)}
                    aria-label={`Move task ${index + 1} earlier`}
                  >
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move task later">
                <span>
                  <IconButton
                    size="small"
                    disabled={disabled || index === selectedTaskIds.length - 1}
                    onClick={() => moveTask(index, 1)}
                    aria-label={`Move task ${index + 1} later`}
                  >
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove task">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={disabled}
                    onClick={() => removeTask(index)}
                    aria-label={`Remove task ${index + 1}`}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
